import {
  type GetQBankStructureLevelItemsWithQIdByQBankStructureIdQuery,
  type GetQBankStructureLevelsByQBankStructureIdQuery,
  QBankStructureLevelCategory,
  useGetQBankStructureLevelItemsWithQIdByQBankStructureIdQuery,
  useGetQBankStructureLevelsByQBankStructureIdQuery,
} from '@exam-management/graphql';
import { type PropsWithChildren } from '@shared/utils';
import { createContext, useMemo, useState } from 'react';

interface AddQBankStructureToQConfigGeneratorProviderContextType {
  qBankStructureLevelItems: GetQBankStructureLevelItemsWithQIdByQBankStructureIdQuery['qbsGetQBankStructureLevelItemsByQBankStructureId'];
  qBankStructureLevels: GetQBankStructureLevelsByQBankStructureIdQuery['qbsGetQBankStructureLevelsByQBankStructureId'];
  qbankStructureId: string;
  isLoading?: boolean;
  setQbankStructureId: (id: string) => void;
}

export const AddQBankStructureToQConfigGeneratorProviderContext = createContext<
  AddQBankStructureToQConfigGeneratorProviderContextType | undefined
>(undefined);

export default function AddQBankStructureToQConfigGeneratorProvider(
  props: Readonly<PropsWithChildren>,
) {
  const { children } = props;
  const [qbankStructureId, setQbankStructureId] = useState<string>('');

  const { data: getQBSLevelItemsResult, loading } =
    useGetQBankStructureLevelItemsWithQIdByQBankStructureIdQuery({
      variables: {
        qBankStructureId: qbankStructureId,
        options: {
          categories: [
            QBankStructureLevelCategory.Part,
            QBankStructureLevelCategory.Subject,
            QBankStructureLevelCategory.Group,
            QBankStructureLevelCategory.Chapter,
          ],
        },
      },
      skip: !qbankStructureId,
    });

  const qBankStructureLevelItems = useMemo(
    () => getQBSLevelItemsResult?.qbsGetQBankStructureLevelItemsByQBankStructureId ?? [],
    [getQBSLevelItemsResult],
  );

  const { data: getQBSLevelResult } = useGetQBankStructureLevelsByQBankStructureIdQuery({
    variables: {
      qBankStructureId: qbankStructureId || '',
    },
    skip: !qbankStructureId,
  });

  const qBankStructureLevels = useMemo(
    () => getQBSLevelResult?.qbsGetQBankStructureLevelsByQBankStructureId ?? [],
    [getQBSLevelResult],
  );

  const values: AddQBankStructureToQConfigGeneratorProviderContextType = useMemo(() => {
    return {
      qBankStructureLevelItems,
      qBankStructureLevels,
      qbankStructureId,
      isLoading: loading,
      setQbankStructureId,
    };
  }, [
    qBankStructureLevelItems,
    qBankStructureLevels,
    qbankStructureId,
    loading,
    setQbankStructureId,
  ]);

  return (
    <AddQBankStructureToQConfigGeneratorProviderContext.Provider value={values}>
      {children}
    </AddQBankStructureToQConfigGeneratorProviderContext.Provider>
  );
}
